/* ONYX account-time client: no local totals or browser-storage fallback. */
(() => {
  if (window.OnyxStudyTime) return;
  const IDLE_MS = 7_200_000, HEARTBEAT_MS = 15_000;
  let shared;
  class Tracker {
    constructor(endpoint) {
      this.endpoint = new URL(endpoint, window.location.origin);
      if (this.endpoint.origin !== window.location.origin) throw new Error('Use the same-origin Shopify app proxy.');
      this.listeners = new Set();
      this.view = { signedIn: false, customerId: null, totalMs: 0, status: 'Connecting…' };
      this.token = null; this.seq = 0; this.activitySeq = 0;
      this.lastActivity = performance.now(); this.lastSent = -Infinity;
      this.retryAt = 0; this.lastActive = false; this.busy = false; this.stopped = false; this.suspended = false;
      this.videoTimes = new WeakMap(); this.events = new AbortController();
      this.touch = event => {
        if (!event.isTrusted) return;
        this.lastActivity = performance.now(); this.activitySeq++;
      };
      for (const type of ['pointerdown','pointermove','keydown','scroll','touchstart']) {
        window.addEventListener(type, this.touch, { passive: true, signal: this.events.signal });
      }
      document.addEventListener('visibilitychange', () => this.tick(), { signal: this.events.signal });
      window.addEventListener('focus', event => { this.touch(event); this.tick(); }, { signal: this.events.signal });
      window.addEventListener('blur', () => this.tick(), { signal: this.events.signal });
      window.addEventListener('online', () => { this.retryAt = 0; this.tick(); }, { signal: this.events.signal });
      window.addEventListener('pagehide', () => { this.suspended = true; this.tick(); }, { signal: this.events.signal });
      window.addEventListener('pageshow', () => { this.suspended = false; this.tick(); }, { signal: this.events.signal });
      this.timer = setInterval(() => this.tick(), 1000);
    }
    publish(data) {
      this.view = { ...this.view, ...data };
      for (const listener of this.listeners) listener(this.view);
    }
    movingVideo() {
      return [...document.querySelectorAll('video')].some(video => {
        const before = this.videoTimes.get(video);
        this.videoTimes.set(video, video.currentTime);
        return !video.paused && !video.ended && video.readyState >= 2 && before !== undefined &&
          video.currentTime > before && video.currentTime - before < 5;
      });
    }
    active() {
      if (this.suspended || document.visibilityState !== 'visible' || !document.hasFocus()) return false;
      if (this.movingVideo()) { this.lastActivity = performance.now(); this.activitySeq++; }
      return performance.now() - this.lastActivity < IDLE_MS;
    }
    async request(body) {
      const controller = new AbortController(); this.requestController = controller;
      const timeout = setTimeout(() => controller.abort(), 12_000);
      try {
        const response = await fetch(this.endpoint.href, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error',
          signal: controller.signal, keepalive: !!body,
          ...(body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
        });
        if (!response.ok) { const error = new Error('Study sync unavailable'); error.status = response.status; throw error; }
        const result = await response.json();
        if (typeof result.signedIn !== 'boolean' || !Number.isFinite(result.totalMs) || result.totalMs < 0) throw new Error('Invalid study response');
        return result;
      } finally { clearTimeout(timeout); }
    }
    async tick() {
      if (this.stopped) return;
      const active = this.active(), now = performance.now();
      if (this.busy || now < this.retryAt) return;
      if (!this.token && (this.suspended || document.visibilityState !== 'visible')) return;
      if (this.token && active === this.lastActive && now - this.lastSent < HEARTBEAT_MS) return;
      this.busy = true;
      try {
        if (!this.token) {
          const result = await this.request();
          if (this.stopped) return;
          if (!result.signedIn) {
            this.publish({ signedIn: false, customerId: null, totalMs: 0, status: 'Sign in to earn progress' });
            this.retryAt = performance.now() + 60_000; return;
          }
          if (result.paused) {
            this.publish({ signedIn: true, customerId: result.customerId, totalMs: result.totalMs, status: 'Study time paused · maintenance' });
            this.retryAt = performance.now() + 15_000; return;
          }
          if (typeof result.token !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(result.token)) throw new Error('Invalid tracker lease');
          this.token = result.token; this.seq = 0; this.lastSent = -Infinity;
          this.publish({ signedIn: true, customerId: result.customerId, totalMs: result.totalMs, status: 'Connecting…' });
        } else {
          const token = this.token;
          const body = { token, seq: ++this.seq, activitySeq: this.activitySeq, active };
          this.lastSent = now; this.lastActive = active;
          const result = await this.request(body);
          if (this.stopped || token !== this.token) return;
          if (!result.signedIn) {
            this.token = null;
            this.publish({ signedIn: false, customerId: null, totalMs: 0, status: 'Sign in to earn progress' });
            this.retryAt = performance.now() + 60_000; return;
          }
          this.publish({ signedIn: true, customerId: result.customerId, totalMs: result.totalMs,
            status: active && result.active ? 'Studying' : 'Paused' });
        }
      } catch (error) {
        if (this.stopped) return;
        this.publish({ status: 'Study time paused · reconnecting' });
        if (error.status === 401 || error.status === 403) {
          // An expired lease/server recovery is not a progress reset. Ask Shopify
          // for the current identity again; only a confirmed guest response clears it.
          this.token = null;
        }
        this.retryAt = performance.now() + 15_000;
      } finally { this.busy = false; }
    }
    stop() {
      this.stopped = true; clearInterval(this.timer); this.events.abort(); this.requestController?.abort();
    }
  }
  window.OnyxStudyTime = {
    subscribe(listener, endpoint = '/apps/onyx-study') {
      if (!shared) shared = new Tracker(endpoint);
      const tracker = shared;
      tracker.listeners.add(listener); listener(tracker.view); tracker.tick();
      return () => {
        tracker.listeners.delete(listener);
        if (!tracker.listeners.size) { tracker.stop(); if (shared === tracker) shared = null; }
      };
    },
  };
})();

(() => {
  const dailyMotivation = ["Show up today. Build confidence for tomorrow.","One lesson today. Stronger judgment tomorrow.","Small steps build lasting knowledge.","Keep learning. Your effort adds up.","Progress starts with showing up.","Give yourself time to get better.","Learn one thing. Carry it forward.","Your future is worth the effort.","Stay curious. Keep building.","A little progress still counts.","Keep going. Understanding takes practice.","Build the knowledge you can trust.","Make today a step forward.","Consistency turns effort into skill.","You are building something that lasts.","Return to it. Make it clearer.","Every focused minute moves you forward.","Stay patient with your progress.","Put in the effort. Let it grow.","Keep sharpening your judgment.","Today is another chance to improve.","Take your time. Learn it well.","Strong habits build strong inspectors.","Keep showing up for yourself.","Your knowledge grows with your effort.","One clear understanding beats rushing.","Bring your focus. Build your confidence.","Start where you are. Keep growing.","A steady pace still gets you there.","Learn today. Apply it tomorrow.","Keep building. You are making progress."];
  const limits = [0, 22.5, 45, 90, 225, 360, 720];
  const palette = [
    ['#926441','#b68c6b','#62442e'], ['#c08b60','#dbb391','#895b3c'],
    ['#a5a39c','#c9c7bf','#76756f'], ['#85898c','#b4b7b9','#5b5f62'],
    ['#bac6ce','#e0e7eb','#87969f'], ['#4f5458','#83898e','#292d30'],
    ['#111317','#bec6ce','#030406']
  ];
  const rankFor = (hours) => Math.max(0, limits.findLastIndex(n => hours >= n));
  class OnyxStudyRank extends HTMLElement {
    connectedCallback() {
      if (this.ready) { this.startTracking(); return; }
      this.ready = true;
      this.labels = JSON.parse(this.querySelector('[data-osr-labels]').textContent);
      this.dialog = this.querySelector('dialog');
      this.openButton = this.querySelector('.osr__open');
      this.openButton.addEventListener('click', () => this.dialog.showModal());
      this.querySelector('.osr__close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => this.openButton.focus());
      this.dialog.addEventListener('click', e => {
        if (e.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.dialog.close();
      });
      const tiers = this.querySelector('.osr__tiers');
      limits.forEach((hours, i) => {
        const li = document.createElement('li');
        const gem = document.createElement('span');
        gem.className = 'osr__gem';
        gem.setAttribute('aria-hidden', 'true');
        this.color(li, i);
        const title = document.createElement('strong');
        title.textContent = this.labels.names[i];
        const value = document.createElement('span');
        value.textContent = `${hours.toLocaleString()} h`;
        li.append(gem, title, value); tiers.append(li);
      });
      this.render(0);
      this.startTracking();
    }

    startTracking() {
      this.unsubscribe = window.OnyxStudyTime.subscribe(view => {
        this.render(view.signedIn ? view.totalMs / 3600000 : 0);
        const status = this.querySelector('[data-osr-status]');
        if (status) status.textContent = view.status;
      }, this.dataset.trackingUrl || '/apps/onyx-study');
    }
    disconnectedCallback() {
      this.unsubscribe?.();

    }

    color(element, i) {
      const copper = i < 5;
      element.style.setProperty('--rank-fire', '0');
      const image = i === 6 ? this.dataset.fireImage : copper ? this.dataset.copperImage : this.dataset.onyxImage;
      if (image) element.style.setProperty('--rank-image', `url(${JSON.stringify(image)})`);
      element.style.setProperty('--rank-size', copper ? '492.3% 154.2%' : '179.65% 128.86%');
      element.style.setProperty('--rank-position', copper ? '49.75% 44.17%' : '49.34% 40.99%');
      element.style.setProperty('--rank-finish', ['sepia(.25) saturate(.7) brightness(1.05)','saturate(1.15) brightness(1.1)','grayscale(1) sepia(.2) brightness(1.4) contrast(.5)','grayscale(1) brightness(.85) contrast(.75)','grayscale(1) brightness(2) contrast(.65)','brightness(1.7) contrast(.6)','none'][i]);
      if (i === 6) {
        element.style.setProperty('--rank-size', '156.34% 118.7%');
        element.style.setProperty('--rank-position', '49.6% 36.1%');
        element.style.setProperty('--rank-finish', 'none');
      }
      ['--gem','--glint','--shade'].forEach((key,j) => element.style.setProperty(key,palette[i][j]));
    }
    render(value) {
      const today = new Date();
      const day = Math.floor(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) / 86400000);
      const motivation = this.querySelector('[data-osr-motivation]');
      if (motivation) {
        const message = dailyMotivation[((day % dailyMotivation.length) + dailyMotivation.length) % dailyMotivation.length];
        if (motivation.textContent !== message) motivation.textContent = message;
      }
      const hours = Math.max(0, Number.isFinite(value) ? value : 0);
      const i = rankFor(hours), next = limits[i + 1];
      const percent = next ? (hours - limits[i]) / (next - limits[i]) * 100 : 100;
      this.color(this, i);
      document.querySelectorAll('[data-osr-header-gem]').forEach(gem => this.color(gem, i));
      document.querySelectorAll('[data-osr-home]').forEach(link => {
        link.setAttribute('aria-label', 'ONYX Integrity home — your rank: ' + this.labels.names[i]);
        link.setAttribute('title', 'Your rank: ' + this.labels.names[i]);
      });
      this.querySelectorAll('[data-osr-name]').forEach(el => el.textContent = this.labels.names[i]);
      this.querySelectorAll('[data-osr-hours]').forEach(el => el.textContent = `${hours.toLocaleString(undefined, {maximumFractionDigits:2})} ${this.labels.hours.toLowerCase()}`);
      this.querySelectorAll('[data-osr-next]').forEach(el => el.textContent = next ? `${(next-hours).toLocaleString(undefined, {maximumFractionDigits:2})} ${this.labels.next} ${this.labels.names[i+1]}` : this.labels.complete);
      this.querySelector('[data-osr-level]').textContent = `${this.labels.level} ${i+1} ${this.labels.of} 7`;
      this.querySelector('[data-osr-percent]').textContent = `${Math.floor(percent)}%`;
      this.querySelectorAll('progress').forEach(el => el.value = percent);
      this.querySelectorAll('.osr__tiers li').forEach((el,j) => {
        if (i === j) el.setAttribute('aria-current','step'); else el.removeAttribute('aria-current');
      });
    }
  }
  if (!customElements.get('onyx-study-rank')) customElements.define('onyx-study-rank', OnyxStudyRank);
})();

